import { Search, UserRoundCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import EmployeeProfileEditor from "../components/EmployeeProfileEditor";
import { searchUsers } from "../services/api";
import type { HostMode } from "../teams/teamsContext";
import type { MeResponse, UserListItem } from "../types/peopleAdmin";

interface PeopleSearchPageProps {
  me: MeResponse;
  hostMode: HostMode;
}

export default function PeopleSearchPage({ me, hostMode }: PeopleSearchPageProps) {
  const [division, setDivision] = useState(me.divisions[0] || "");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canManage = me.divisions.length > 0;

  const currentRole = useMemo(
    () => me.roles.find((role) => role.division === division),
    [division, me.roles]
  );

  useEffect(() => {
    if (!division) {
      return;
    }

    let cancelled = false;
    async function loadUsers() {
      setLoading(true);
      setError(null);

      try {
        const result = await searchUsers(division, search);
        if (!cancelled) {
          setUsers(result);
          if (selectedUserId && !result.some((user) => user.id === selectedUserId)) {
            setSelectedUserId(null);
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Could not load users.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    const timeout = window.setTimeout(loadUsers, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [division, search, selectedUserId]);

  if (!canManage) {
    return (
      <section className="empty-state">
        <UserRoundCheck size={26} aria-hidden="true" />
        <h2>No assigned divisions</h2>
        <p>Your account is authenticated, but it does not have a People Admin RBAC entry.</p>
      </section>
    );
  }

  return (
    <div className={`people-layout people-layout-${hostMode}`}>
      <section className="search-panel" aria-label="People search">
        <div className="panel-heading">
          <div>
            <h2>People</h2>
            <p>{currentRole?.role || "ProfileEditor"}</p>
          </div>
        </div>

        <label className="field-label" htmlFor="division">
          Division
        </label>
        <select id="division" value={division} onChange={(event) => setDivision(event.target.value)}>
          {me.divisions.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <label className="field-label" htmlFor="people-search">
          Search
        </label>
        <div className="search-input">
          <Search size={18} aria-hidden="true" />
          <input
            id="people-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or email"
          />
        </div>

        {error ? <div className="alert alert-error">{error}</div> : null}

        <div className="result-list" aria-busy={loading}>
          {loading ? <div className="loading-row">Loading...</div> : null}
          {!loading && users.length === 0 ? <div className="loading-row">No people found.</div> : null}
          {users.map((user) => (
            <button
              key={user.id}
              type="button"
              className={`person-row ${selectedUserId === user.id ? "person-row-selected" : ""}`}
              onClick={() => setSelectedUserId(user.id)}
            >
              <span className="person-primary">{user.displayName || user.userPrincipalName}</span>
              <span className="person-secondary">{user.jobTitle || "No job title"}</span>
              <span className="person-tertiary">{user.userPrincipalName || user.mail}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="editor-panel" aria-label="Profile editor">
        {selectedUserId ? (
          <EmployeeProfileEditor
            key={selectedUserId}
            userId={selectedUserId}
            division={division}
            onSaved={() => {
              searchUsers(division, search).then(setUsers).catch(() => undefined);
            }}
          />
        ) : (
          <div className="empty-state editor-empty">
            <UserRoundCheck size={28} aria-hidden="true" />
            <h2>Select an employee</h2>
          </div>
        )}
      </section>
    </div>
  );
}
